import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Eye, Edit, Trash2, Globe } from 'lucide-react';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import apiService from '../../services/api';

const Countries = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    status: 'active'
  });

  useEffect(() => {
    fetchCountries();
  }, [pagination.page, searchTerm, statusFilter]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await apiService.getCountries(params);
      setCountries(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (err) {
      setError('Failed to fetch countries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCountry) {
        await apiService.updateCountry(editingCountry.id, formData);
      } else {
        await apiService.createCountry(formData);
      }

      toast.success(`Country ${editingCountry ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingCountry(null);
      resetForm();
      fetchCountries();
    } catch (err) {
      toast.error(err.message || 'Operation failed');
    }
  };

  const handleView = (country) => {
    setSelectedCountry(country);
    setShowViewModal(true);
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name,
      status: country.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      try {
        await apiService.deleteCountry(id);
        toast.success('Country deleted successfully');
        fetchCountries();
      } catch (err) {
        toast.error(err.message || 'Failed to delete country');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      status: 'active'
    });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCountry(null);
    resetForm();
  };

  const handleAddCountry = () => {
    setEditingCountry(null);
    resetForm();
    setShowModal(true);
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status' },
    {
      key: 'actions',
      label: 'Actions',
      render: (country) => (
        <div className="action-buttons">
          <motion.button
            className="btn-sm touch-target"
            onClick={() => handleView(country)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View Details"
          >
            <Eye size={12} />
          </motion.button>
          <motion.button
            className="btn-sm warning touch-target"
            onClick={() => handleEdit(country)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Edit"
          >
            <Edit size={12} />
          </motion.button>
          <motion.button
            className="btn-sm danger touch-target"
            onClick={() => handleDelete(country.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Delete"
          >
            <Trash2 size={12} />
          </motion.button>
        </div>
      )
    }
  ];

  return (
    <div className="countries-management">
      <div className="page-header">
        <h2><Globe size={24} /> Countries Management</h2>
        <motion.button
          className="btn-primary"
          onClick={handleAddCountry}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add Country
        </motion.button>
      </div>

      <DataTable
        data={countries}
        columns={columns}
        loading={loading}
        emptyMessage="No countries found"
      />

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Country Details"
      >
        {selectedCountry && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                <Globe size={40} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedCountry.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>Country</p>
            </div>

            {/* Details Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <Globe size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151', fontWeight: '500' }}>Name:</span>
                  <span style={{ color: '#374151' }}>{selectedCountry.name}</span>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedCountry.status === 'active' ? '#dcfce7' : '#fef3c7',
                color: selectedCountry.status === 'active' ? '#166534' : '#92400e',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedCountry.status === 'active' ? '#22c55e' : '#f59e0b',
                  borderRadius: '50%'
                }} />
                {selectedCountry.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title={`${editingCountry ? 'Edit' : 'Add'} Country`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingCountry ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Countries;